/**
 * ToggleBlock — Collapsible content block (like Notion's toggle).
 * Summary line uses content: 'inline', body uses NestedEditor sub-editor.
 */

import { useState, useCallback, lazy, Suspense } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { ChevronRight } from 'lucide-react';

const NestedEditor = lazy(() => import('./NestedEditor'));

interface ToggleBlockRendererProps {
  block: {
    id: string;
    props: { isOpen: string; childContent: string };
  };
  editor: {
    updateBlock: (id: string, update: { props: Partial<{ isOpen: string; childContent: string }> }) => void;
    isEditable: boolean;
  };
  contentRef: React.RefObject<HTMLDivElement | null>;
}

function ToggleBlockRenderer({ block, editor, contentRef }: ToggleBlockRendererProps) {
  const isOpen = block.props.isOpen === 'true';
  const isEditable = editor.isEditable;

  const toggleOpen = useCallback(() => {
    editor.updateBlock(block.id, {
      props: { isOpen: isOpen ? 'false' : 'true' },
    });
  }, [block.id, editor, isOpen]);

  const handleChildChange = useCallback(
    (content: string) => {
      editor.updateBlock(block.id, { props: { childContent: content } });
    },
    [block.id, editor]
  );

  return (
    <div className="toggle-block my-1" contentEditable={false}>
      {/* Summary row — clickable chevron + inline editable content */}
      <div className="flex items-start gap-1">
        <button
          type="button"
          onClick={toggleOpen}
          className="mt-1 p-0.5 rounded hover:bg-stone-100 transition-colors shrink-0"
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            className={`w-4 h-4 text-stone-400 transition-transform duration-150 ${
              isOpen ? 'rotate-90' : ''
            }`}
          />
        </button>
        <div
          ref={contentRef}
          className="flex-1 min-w-0 text-sm outline-none py-0.5"
          contentEditable={isEditable}
          suppressContentEditableWarning
        />
      </div>

      {/* Nested content — only rendered when open */}
      {isOpen && (
        <div className="ml-6 mt-1 pl-3 border-l-2 border-stone-200">
          <Suspense fallback={<div className="text-xs text-stone-400 py-2">Loading...</div>}>
            <NestedEditor
              initialContent={block.props.childContent || ''}
              onChange={handleChildChange}
              editable={isEditable}
              placeholder="Toggle content..."
              debounceMs={400}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

// ── Block spec ────────────────────────────────────────────────────────────

export const toggleBlockSpec = createReactBlockSpec(
  {
    type: 'toggle' as const,
    propSchema: {
      isOpen: { default: 'true' },
      childContent: { default: '' },
    },
    content: 'inline',
  },
  {
    render: (props) => {
      const blockProps: ToggleBlockRendererProps = {
        block: {
          id: props.block.id,
          props: props.block.props as { isOpen: string; childContent: string },
        },
        editor: {
          updateBlock: (id, update) => (props.editor as any).updateBlock(id, update),
          isEditable: (props.editor as any).isEditable,
        },
        contentRef: props.contentRef as React.RefObject<HTMLDivElement | null>,
      };
      return <ToggleBlockRenderer {...blockProps} />;
    },
  }
);

export default toggleBlockSpec;
